import type {
  Entitlement,
  Slots,
} from '@collinsonx/utils/generatedTypes/graphql';

import { getItem, JSONParse, setItem } from '@lib';
import { BOOKING_MODE } from 'config/constants';
import { GUEST_TYPES } from 'config/productDefaultConfig';
import { BOOKING_ID, BOOKING_MODE_STATE } from 'config/storage';
import useProduct from 'hooks/useProduct';
import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ValuationPriceBreakdown } from 'types/mappedProductData';
import { clientIsReady } from 'utils/errorHandling';
import { getTaxableGuestsCount } from 'utils/product';

// Snapshot of the latest successful `useValuationPrice` result. Written on
// the `check-slots` page and read by every downstream `TotalPriceBlock`
// render (FlightDetailsAndGuests, confirm-payment, confirm-booking,
// cancel-booking) so the per-visitor-type breakdown is available without
// re-fetching the valuation on each page. Serializable via JSON so it
// survives the `bookingdetail` session-storage roundtrip.
export interface ValuationSnapshot {
  coveredByEntitlement: (boolean | null)[] | null;
  currency: string | null;
  priceBreakdown: (ValuationPriceBreakdown | null)[] | null;
  totalPrice: number | null;
  totalPriceFormatted: string | null;
}

export interface Booking {
  adults: number;
  airport: string;
  alwaysAvailable?: boolean;
  amendmentID?: string;
  arrival?: string;
  arrivalTime?: string;
  availableEntitlements: number;
  bookingDetails?: {
    adults: number;
    bookingId: string;
    children: number;
    currentPrice: number;
    departureDate: Date | null;
    existing_booking_slot: string;
    flightNumber: string;
    flightTime: string;
    infants: number;
    reference: string;
    taxableGuestsCount: number;
    usedEntitlements: number;
  };
  bookingId?: string;
  carrierCode?: string;
  children: number;
  currentPrice: number | undefined;
  departureDate: Date | null;
  departureTime: null | string;
  entitlementsAreAmended: boolean;
  existing_booking_slot: string;
  flightNumber: string;
  flightTime: string;
  guestsCount: number;
  infants: number;
  partnerMetadata?: {
    [key: string]: boolean | Date | null | number | string | undefined;
    alwaysAvailable?: boolean;
  };
  paymentWillBeAppliedToAmend: boolean;
  pendingPayment: boolean;
  priceIsAmended: boolean;
  quantity: number;
  reference: string | undefined;
  selectedEntitlementsCount: number;
  selectedSlot?: Slots;
  seniors: number;
  slotConfigurationId?: string;
  taxableGuestsCount: number;
  usedEntitlementsCount: number;
  valuationSnapshot?: ValuationSnapshot;
}

export interface BookingContextProps {
  booking: Booking;
  entitlements: Entitlement[];
  isLoadingEntitlements: boolean;
  setBooking: (booking: {}) => void;
  setEntitlements: (val: Entitlement[]) => void;
  setEntitlementsLoading: (val: boolean) => void;
}

const setBookingStorage = (booking: Booking) => {
  if (!clientIsReady) return;

  sessionStorage.setItem('bookingdetail', JSON.stringify(booking));
};

const getBookingStorage = () => {
  if (!clientIsReady) return {};

  const storageObject = JSONParse(sessionStorage.getItem('bookingdetail'), {});

  return {
    ...defaultPropertyValues,
    ...storageObject,
  };
};

const defaultPropertyValues = {
  adults: 1,
  airport: '',
  alwaysAvailable: false,
  availableEntitlements: 0,
  children: 0,
  departureDate: null,
  departureTime: null,
  flightNumber: '',
  infants: 0,
  paymentWillBeAppliedToAmend: false,
  pendingPayment: false,
  priceIsAmended: false,
  quantity: 0,
  selectedEntitlementsCount: 0,
  seniors: 0,
  taxableGuestsCount: 1,
  usedEntitlements: 0,
};

const defaultContext = {
  booking: getBookingStorage(),
  entitlements: [],
  isLoadingEntitlements: false,
  setBooking: () => {},
  setEntitlements: () => {},
  setEntitlementsLoading: () => {},
};

export const BookingContext =
  createContext<BookingContextProps>(defaultContext);

const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { guests } = useProduct();
  const [isLoadingEntitlements, setEntitlementsLoading] = useState(false);
  const mode = getItem(BOOKING_MODE_STATE);

  const setBookingInterceptor = (booking: Booking): Booking => {
    const {
      adults,
      airport,
      bookingDetails,
      children: childrenCount,
      departureDate,
      departureTime,
      entitlementsAreAmended,
      infants,
      selectedEntitlementsCount,
      seniors,
    } = booking;

    const selectedGuestsCount = {
      [GUEST_TYPES.adults]: adults,
      [GUEST_TYPES.children]: childrenCount,
      [GUEST_TYPES.infants]: infants,
      [GUEST_TYPES.seniors]: seniors,
    };

    const taxableGuestsCount = getTaxableGuestsCount(
      selectedGuestsCount,
      guests
    );

    const pendingPayment = taxableGuestsCount > selectedEntitlementsCount;
    const quantity = taxableGuestsCount - selectedEntitlementsCount;

    const priceIsAmended =
      mode === BOOKING_MODE.EDIT &&
      (bookingDetails?.taxableGuestsCount || 0) - taxableGuestsCount !== 0;

    const paymentWillBeAppliedToAmend =
      (mode === BOOKING_MODE.EDIT && priceIsAmended) || entitlementsAreAmended;

    return {
      ...booking,
      airport,
      alwaysAvailable: Boolean(booking.partnerMetadata?.alwaysAvailable),
      departureDate: departureDate ? new Date(departureDate) : null,
      departureTime,
      paymentWillBeAppliedToAmend,
      pendingPayment,
      quantity,
      selectedEntitlementsCount: selectedEntitlementsCount || 0,
      taxableGuestsCount,
    };
  };

  const [bookingState, setBookingState] = useState<Booking>(
    setBookingInterceptor(getBookingStorage())
  );
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);

  const setBooking = (value: Partial<Booking>) => {
    if (value.bookingId) setItem(BOOKING_ID, value.bookingId);
    setBookingState((oldBookingState) =>
      setBookingInterceptor({
        ...oldBookingState,
        ...value,
      })
    );
  };

  useEffect(() => {
    setBookingStorage(bookingState);
  }, [bookingState]);

  const memoizedContextValue = useMemo(
    () => ({
      booking: bookingState,
      isLoadingEntitlements,
      setBooking,
      setEntitlementsLoading,
    }),
    [bookingState, setBooking, isLoadingEntitlements, setEntitlementsLoading]
  );

  return (
    <BookingContext.Provider
      value={{
        ...memoizedContextValue,
        entitlements,
        setEntitlements,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

export default BookingProvider;
